"use client";

// React & Three.js imports
import {
  AccumulativeShadows,
  Caustics,
  Environment,
  GizmoHelper,
  GizmoViewport,
  OrbitControls,
  RandomizedLight,
} from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { useControls } from "leva";

// Custom hooks
import { useCrystalShell } from "@/hooks/laser-engraving/useCrystalShell";
import { useCausticConfig } from "@/hooks/laser-engraving/useCausticConfig";
import { useSmartShadowSystem } from "@/hooks/laser-engraving/useShadowSystem";
import { useSceneOptics } from "@/hooks/laser-engraving/useSceneOptics";
import { useInnerModel } from "@/hooks/laser-engraving/useInnerModel";
import { usePointCloudGenerator } from "@/hooks/laser-engraving/usePointCloudGenerator";
import { usePointCloudReadiness } from "@/hooks/laser-engraving/usePointCloudReadiness";
import { useSceneControls } from "@/hooks/useSceneControls";

// Custom components
import { LaserEngraving } from "@/components/laser-engraving";
import { PageTitle } from "@/components/rendering/PageTitle";

// Types and constants
import {
  AUTO_ROTATION_CONFIG,
  CAMERA_CONFIG,
  DEFAULT_GLTF_CONFIG,
  DEFAULT_POINT_CLOUD_CONFIG,
  ENVIRONMENT_CONFIG,
  LASER_PHYSICS_CONSTANTS,
  LIGHTING_CONFIG,
  DEFAULT_SHADOW_CONFIG,
} from "@/lib/laser-physics";
import { DisplayMode } from "@/types/laser-engraving";

/**
 * 激光内雕页面
 *
 * 使用新的组件架构和Hook系统，提供：
 * - 模块化的组件设计
 * - 清晰的职责分离
 * - 更好的可维护性
 * - 优化的性能
 * - 完善的错误处理
 */
export default function LaserEngravingPage() {
  // 🎯 通用场景控制
  const sceneConfig = useSceneControls({
    defaultAutoRotate: AUTO_ROTATION_CONFIG.enabled,
    defaultAutoRotateSpeed: AUTO_ROTATION_CONFIG.speed,
    defaultCameraFov: CAMERA_CONFIG.fov,
    defaultAmbientLightIntensity: 0.5,
    autoRotateSpeedRange: AUTO_ROTATION_CONFIG.speedRange,
    cameraFovRange: [20, 80],
    ambientLightIntensityRange: [0, 2],
  });

  // 🎯 水晶外壳控制
  const { config: crystalConfig } = useCrystalShell("Crystal Settings");

  // 🎯 场景光学效果统一配置 - Shadow 和 Caustic 系统共享的物理参数
  const { config: sceneOptics } = useSceneOptics("Scene Optics", {
    effectPlane: [0, -0.5, 0],
  });

  // 🎯 点云控制面板 - 集成显示模式切换和参数调节
  const pointCloudConfig = useControls("Point Cloud Settings", {
    showPointCloud: {
      value: DEFAULT_POINT_CLOUD_CONFIG.showPointCloud,
    },
    pointSize: {
      value: DEFAULT_POINT_CLOUD_CONFIG.pointSize,
      min: 0.01,
      max: 0.2,
      step: 0.001,
    },
    pointCount: {
      value: DEFAULT_POINT_CLOUD_CONFIG.pointCount,
      min: 1000,
      max: 50000,
      step: 1000,
    },
    sizeMultiplier: {
      value: DEFAULT_POINT_CLOUD_CONFIG.sizeMultiplier,
      min: 0.05,
      max: 0.5,
      step: 0.01,
    },
    opacity: {
      value: DEFAULT_POINT_CLOUD_CONFIG.opacity,
      min: 0.1,
      max: 1.0,
      step: 0.01,
    },
  });

  // 确定显示模式
  const displayMode: DisplayMode = pointCloudConfig.showPointCloud
    ? DisplayMode.POINT_CLOUD
    : DisplayMode.MODEL;

  console.log("🎯 LaserEngravingPage 渲染", {
    isClient: typeof window !== "undefined",
    displayMode,
  });

  // 🎯 内部模型处理 - 统一获取所有必需数据（仅在客户端执行）
  const shouldLoadModel =
    typeof window !== "undefined" && // 仅在客户端执行
    (displayMode === DisplayMode.POINT_CLOUD ||
      displayMode === DisplayMode.MODEL);
  const {
    sourceGeometry,
    processedModels,
    isLoading: isLoadingModel,
    error: modelError,
  } = useInnerModel(shouldLoadModel, crystalConfig.size, DEFAULT_GLTF_CONFIG);

  // 🎯 点云生成 - 基于源几何体生成点云（仅在客户端且有源几何体时执行）
  const shouldGeneratePointCloud =
    typeof window !== "undefined" && // 仅在客户端执行
    displayMode === DisplayMode.POINT_CLOUD &&
    !!sourceGeometry; // 确保有源几何体
  const {
    pointGeometry,
    isGenerating: isGeneratingPointCloud,
    isFullyInitialized,
    error: pointCloudError,
    generatedPointCount,
  } = usePointCloudGenerator(
    sourceGeometry,
    pointCloudConfig,
    LASER_PHYSICS_CONSTANTS,
    shouldGeneratePointCloud,
  );

  // 🎯 点云就绪检测 - 确保点云完全初始化
  const pointCloudResult = {
    pointGeometry,
    isGenerating: isGeneratingPointCloud,
    isFullyInitialized,
    error: pointCloudError,
    generatedPointCount,
  };
  const { isPointCloudReady } = usePointCloudReadiness(
    pointCloudResult,
    displayMode,
  );

  // 🎯 智能阴影系统 - 基于点云就绪状态控制阴影，集成 leva 配置面板
  const { config: shadowConfig, shadowsReady } = useSmartShadowSystem(
    sceneOptics,
    isPointCloudReady,
    "Shadow Settings",
  );

  // 🎯 焦散效果控制
  const { config: causticConfig } = useCausticConfig(
    sceneOptics,
    "Caustic Settings",
  );

  // 🎯 统一错误和加载状态
  const combinedError = modelError || pointCloudError;
  const isLoading = isLoadingModel || isGeneratingPointCloud;

  console.log("🎯 当前显示模式:", displayMode, {
    showPointCloud: pointCloudConfig.showPointCloud,
    isPointCloudReady,
    shadowsReady,
    crystalSize: crystalConfig.size,
    isLoading,
    combinedError,
    pointCloudStatus: {
      hasGeometry: !!pointGeometry,
      isGenerating: isGeneratingPointCloud,
      isFullyInitialized,
      generatedPointCount,
    },
    modelStatus: {
      hasProcessedModels: processedModels.length > 0,
      isLoadingModel,
      modelError,
    },
  });

  return (
    <div className="w-full h-full bg-background">
      {/* 页面标题 */}
      <PageTitle
        title="水晶激光内雕"
        subtitle="MeshTransmissionMaterial + 点云技术"
        subtitleClassName="text-muted-foreground"
      />

      {/* 3D 渲染场景 */}
      <Canvas
        shadows
        camera={{
          position: CAMERA_CONFIG.position,
          fov: sceneConfig.cameraFov,
        }}
      >
        {/* 场景背景 */}
        <color
          attach="background"
          args={[ENVIRONMENT_CONFIG.backgroundColor]}
        />

        {/* 🎯 光照系统 - 复杂光照适合折射效果 */}
        <ambientLight
          intensity={
            sceneConfig.ambientLightIntensity *
            LIGHTING_CONFIG.ambientLightMultiplier
          }
        />
        <spotLight
          decay={LIGHTING_CONFIG.spotLight.decay}
          position={LIGHTING_CONFIG.spotLight.position}
          angle={LIGHTING_CONFIG.spotLight.angle}
          penumbra={LIGHTING_CONFIG.spotLight.penumbra}
        />
        <pointLight
          decay={LIGHTING_CONFIG.pointLight.decay}
          position={LIGHTING_CONFIG.pointLight.position}
        />

        {/* ================== 主要渲染对象 ================== */}

        {/* 🎯 条件渲染：根据焦散开关决定是否包装Caustics组件 */}
        {causticConfig.enabled && sceneOptics.enabled ? (
          <Caustics
            causticsOnly={causticConfig.causticsOnly}
            backside={causticConfig.backside}
            color={causticConfig.color}
            position={sceneOptics.effectPlane}
            lightSource={sceneOptics.lightSource}
            worldRadius={causticConfig.worldRadius}
            ior={causticConfig.ior}
            backsideIOR={causticConfig.backsideIOR}
            intensity={causticConfig.intensity}
          >
            {/* 🎯 激光内雕组合组件 - 传递完整的预处理数据 */}
            <LaserEngraving
              position={[0, 0.75, 0]}
              rotation={[0, Math.PI / 6, 0]}
              displayMode={displayMode}
              pointCloudConfig={pointCloudConfig}
              crystalConfig={crystalConfig}
              // 🎯 传递预处理的数据，避免重复获取
              pointGeometry={pointGeometry}
              processedModels={processedModels}
              isLoading={isLoading}
              error={combinedError}
            />
          </Caustics>
        ) : (
          /* 🎯 焦散禁用时：直接渲染激光内雕，跳过Caustics包装以提升性能 */
          <LaserEngraving
            position={[0, 0.25, 0]}
            rotation={[0, Math.PI / 6, 0]}
            displayMode={displayMode}
            pointCloudConfig={pointCloudConfig}
            crystalConfig={crystalConfig}
            // 🎯 传递预处理的数据，避免重复获取
            pointGeometry={pointGeometry}
            processedModels={processedModels}
            isLoading={isLoading}
            error={combinedError}
          />
        )}

        {/* ================== 场景系统 ================== */}

        {/* 🎯 智能阴影系统 - 基于点云完全就绪状态控制，使用统一的场景光学配置 */}
        {shadowsReady && shadowConfig.enabled && sceneOptics.enabled && (
          <AccumulativeShadows
            temporal
            frames={shadowConfig.accumulative.frames}
            color={shadowConfig.accumulative.color}
            colorBlend={shadowConfig.accumulative.colorBlend}
            toneMapped={shadowConfig.accumulative.toneMapped}
            alphaTest={shadowConfig.accumulative.alphaTest}
            opacity={shadowConfig.accumulative.opacity}
            scale={shadowConfig.accumulative.scale}
            position={sceneOptics.effectPlane}
          >
            <RandomizedLight
              amount={shadowConfig.randomizedLight.amount}
              radius={shadowConfig.randomizedLight.radius}
              ambient={shadowConfig.randomizedLight.ambient}
              position={sceneOptics.lightSource}
              bias={shadowConfig.randomizedLight.bias}
            />
          </AccumulativeShadows>
        )}

        {/* 🎯 环境贴图 */}
        <Environment files={ENVIRONMENT_CONFIG.defaultHDR} />

        {/* 🎯 相机控制 */}
        <OrbitControls
          makeDefault
          autoRotate={sceneConfig.autoRotate}
          autoRotateSpeed={sceneConfig.autoRotateSpeed}
          minPolarAngle={CAMERA_CONFIG.polarAngle.min}
          maxPolarAngle={CAMERA_CONFIG.polarAngle.max}
        />

        {/* 🎯 坐标系显示 - 左上角显示XYZ轴向，避开按钮遮挡 */}
        <GizmoHelper alignment="top-left" margin={[80, 140]}>
          <GizmoViewport
            axisColors={["#ff0000", "#00ff00", "#0000ff"]}
            labelColor="white"
          />
        </GizmoHelper>
      </Canvas>
    </div>
  );
}
